













































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { CommandDto, CommandResultDto } from '@/shared/dtos/agents-admin';

@Component
export default class CommandsTab extends Vue {
    @Prop() public commands: CommandDto[] = [];
    @Prop() public commandResults: CommandResultDto[] = [];
    @Prop() public loading: boolean = false;

    public selectedCommandId: string = '';

    public get sortedCommands(): CommandDto[] {
        return [...this.commands].sort((a, b) => {
            const timeA = a.created_at ? a.created_at.getTime() : 0;
            const timeB = b.created_at ? b.created_at.getTime() : 0;
            return timeB - timeA;
        });
    }

    public get commandsByAgent(): { [key: string]: CommandDto[] } {
        const grouped: { [key: string]: CommandDto[] } = {};
        this.sortedCommands.forEach(cmd => {
            const agentId = cmd.agent_id || 'Sin agente';
            if (!grouped[agentId]) grouped[agentId] = [];
            grouped[agentId].push(cmd);
        });
        return grouped;
    }

    public get groupedAgents(): string[] {
        return Object.keys(this.commandsByAgent).sort();
    }

    public get selectedCommand(): CommandDto | null {
        if (this.sortedCommands.length === 0) return null;
        const found = this.sortedCommands.find(c => c.command_id === this.selectedCommandId);
        return found || this.sortedCommands[0];
    }

    public get selectedResult(): CommandResultDto | null {
        const cmd = this.selectedCommand;
        if (!cmd) return null;
        const found = this.commandResults.find(r => r.command_id === cmd.command_id);
        return found || null;
    }

    public get selectedSql(): string {
        if (!this.selectedCommand) return '';
        return this.extractSql(this.selectedCommand.payload_json);
    }

    public get completedCount(): number {
        return this.commands.filter(c => c.status === 4).length;
    }

    public get pendingCount(): number {
        return this.commands.filter(c => c.status === 0).length;
    }

    public get failedCount(): number {
        return this.commands.filter(c => c.status === 5).length;
    }

    public get resultRows(): any[] {
        if (!this.selectedResult) return [];
        const data: any = this.selectedResult.result_data;
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data.rows)) return data.rows;
        return [];
    }

    public get resultPreviewRows(): any[] {
        return this.resultRows.slice(0, 50);
    }

    public get resultTableHeaders(): Array<{ text: string; value: string; sortable: boolean }> {
        if (this.resultPreviewRows.length === 0) return [];
        const first = this.resultPreviewRows[0] || {};
        return Object.keys(first).map(key => ({ text: key, value: key, sortable: true }));
    }

    @Watch('commands', { immediate: true, deep: true })
    public onCommandsChanged() {
        if (!this.selectedCommandId && this.sortedCommands.length > 0) {
            this.selectedCommandId = this.sortedCommands[0].command_id;
            return;
        }

        const exists = this.sortedCommands.some(c => c.command_id === this.selectedCommandId);
        if (!exists && this.sortedCommands.length > 0) {
            this.selectedCommandId = this.sortedCommands[0].command_id;
        }
    }

    public getStatusText(status: number): string {
        switch (status) {
            case 0: return 'Pendiente';
            case 4: return 'Completado';
            case 5: return 'Fallido';
            default: return 'Desconocido';
        }
    }

    public getStatusColor(status: number): string {
        switch (status) {
            case 0: return 'info';
            case 4: return 'success';
            case 5: return 'error';
            default: return 'grey';
        }
    }

    public getFinalStatusText(status: number): string {
        switch (status) {
            case 0: return 'Pendiente';
            case 1: return 'Enviado';
            case 2: return 'Aceptado';
            case 3: return 'En ejecución';
            case 4: return 'Éxito';
            case 5: return 'Fallido';
            case 6: return 'Timeout';
            case 7: return 'Rechazado';
            case 8: return 'Cancelado';
            default: return 'Desconocido';
        }
    }

    public shortId(id: string): string {
        if (!id) return '-';
        return id.length > 16 ? `${id.substring(0, 16)}...` : id;
    }

    public formatDateTime(date: Date | null): string {
        if (!date) return 'N/A';
        return new Intl.DateTimeFormat('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).format(date);
    }

    public getExecutionTime(command: CommandDto): string {
        if (!command.started_at || !command.completed_at) return '';
        const diff = command.completed_at.getTime() - command.started_at.getTime();
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) return `${hours}h ${minutes % 60}m`;
        if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
        return `${seconds}s`;
    }

    public extractSql(payloadJson: string): string {
        if (!payloadJson) return '';
        try {
            const parsed = JSON.parse(payloadJson);
            if (parsed && typeof parsed.sql === 'string') return parsed.sql;
            return '';
        } catch (e) {
            return '';
        }
    }

    public formatPayload(payload: string): string {
        if (!payload) return '';
        try {
            return JSON.stringify(JSON.parse(payload), null, 2);
        } catch (e) {
            return payload;
        }
    }

    public formatResult(result: CommandResultDto): string {
        const data = result.result_data || result.result_payload_json || {};
        try {
            if (typeof data === 'string') return JSON.stringify(JSON.parse(data), null, 2);
            return JSON.stringify(data, null, 2);
        } catch (e) {
            return String(data);
        }
    }
}
